import * as React from 'react';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Tag from 'antd/lib/tag';
import Tabs, { TabsProps } from 'antd/lib/tabs';
import Alert from 'antd/lib/alert';

import { ItemProvider, useItemProviderContext } from '@common/react/components/Core/ItemProvider/ItemProvider';
import ServerPageProvider from '@common/react/components/Core/ServerPageProvider/ServerPageProvider';
import { ItemsProvider, SortingDirection } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import { BreadCrumbs } from '@common/react/components/UI/BreadCrumbs/BreadCrumbs';

import { Location } from '@app/objects/Location';
import Locations from '@app/components/Pages/DoctorPortal/Locations';
import { getClinicAvatar } from '@app/components/Pages/Clinics/Clinics';
import '@app/scss/pages/clinicPage.scss';
import '@app/scss/pages/doctorPortal.scss';
import ShareLinks from '@app/components/UI/ShareLinks/ShareLinks';
import DoctorCard from '@app/components/UI/DoctorCard/DoctorCardHome';
import SpecialsCarousel from '@app/components/UI/SpecialsCarousel/SpecialsCarousel';
import { Special } from '@app/objects/Special';
import Reviews, { AllReviews } from '@app/components/Pages/DoctorPortal/Reviews';
import { getLocationName } from '@app/components/Utils';
import SpecialtyTag from '@app/components/Pages/Specialties/SpecialtyTag';
import LoginForm from '@app/components/UI/ScheduleAppointmentModal/LoginForm';
import ScheduleAppointment from '@app/components/UI/ScheduleAppointmentModal/ScheduleAppointment';
import { User } from '@app/objects/User';
import { Doctor } from '@app/objects/Doctor';
import InquiryForm from '@app/components/Forms/InquiryForm';
import { ApplicationState } from '@app/store';
import DoctorAvatar from '@app/components/UI/DoctorCard/DoctorAvatar';
import SocialIcons from '@app/components/UI/SocialIcons/SocialIcons';

const navMenu = [
	{
		title: 'About',
		check: (item: Location) => !!item?.portalDescription,
		id: 'about',
	},
	{
		title: 'Book Online',
		check: (item: Location) => true,
		id: 'bookOnline',
		className: 'show-mobile show-mobile__inline-block',
	},
	{
		title: 'Providers',
		check: (item: Location) => item?.doctors.length > 0,
		id: 'doctors',
	},
	{
		title: 'Specials',
		check: (item: Location) => item?.specials.length > 0,
		id: 'specials',
		className: '',
	},
	{
		title: 'Reviews',
		check: (item: Location) => true,
		id: 'reviews',
	},
];

const ClinicInner: React.FC = () => {
	const context = useItemProviderContext<Location>();
	const location = useLocation();
	const [loginVisible, setLoginVisible] = React.useState(false);
	const [isOpen, setIsOpen] = React.useState<boolean>(false);
	const user = useSelector((state: ApplicationState) => state.login.user);

	if (!context.state) throw 'Need ItemProvider context!';

	const { state: { item } } = context;

	const scheduleDoctors = item.doctors.filter((item) => item.doctor.scheduleAppointment);
	const [doctor, setDoctor] = React.useState<Doctor>(scheduleDoctors?.[0]?.doctor);
	const [tab, setTab] = React.useState(() => (scheduleDoctors.length > 0 ? 'appointment' : 'inquiry'));
	const toggle = () => setIsOpen(!isOpen);

	const getLabel = (label: string, icon: string): React.ReactElement => <div>
		<i className={`fa ${icon}`} style={{ marginRight: '7px' }} />
		{label}
	</div>;

	const getItems: (showMobile: boolean) => TabsProps['items'] = (showMobile) => [
		{
			key: 'appointment',
			label: getLabel('Schedule Appointment', 'fa-calendar'),
			className: 'appointment-tab',
			children: <>
				<div className="contact-text">
					<Alert
						message={<>
							Don’t know procedure you need or convenient time for visit?
							<br />
							<a onClick={() => setTab('inquiry')}>Leave&nbsp;inquiry</a>
							{' '}
							and clinic front desk will contact you soon.
						</>}
						type="info"
					/>
				</div>
				<ScheduleAppointment
					showMobile={showMobile}
					patient={user as unknown as User}
					locationId={item.id}
					locationItems={[item]}
					selectedLocation={item}
					doctor={doctor as unknown as Doctor}
					doctors={scheduleDoctors.map((item) => item.doctor)}
				/>
			</>,
			disabled: !scheduleDoctors.length,
		},
		{
			key: 'inquiry',
			label: getLabel('Send Inquiry', 'fa-envelope'),
			children: <div className="inquiry-wrapper">
				<div className="contact-text">
					<Alert
						message={<>
							{item.doctors.length
								? <>
									Know time and procedure you need?
									<br />
									Just
									{' '}
									<a onClick={() => setTab('appointment')}>Schedule&nbsp;Appointment</a>
									, clinic front desk will contact you soon to confirm it.
								</>
								: <>
									Leave inquiry and clinic front desk will contact you soon for help.
								</>}
							{!user ? <>
								<br />
								Already have account? -
								{' '}
								<a onClick={() => setLoginVisible(true)}>Login</a>
								{' '}
								for fast fill form.
							</> : null}
						</>
						}
						type="info"
					/>
				</div>
				<InquiryForm
					doctorId={doctor?.id}
					doctors={item.doctors.map((item) => item.doctor)}
					locationId={item.id}
					locations={[item]}
				/>
			</div>,
		},
	];

	const name = getLocationName(item, false, true, true) as string;

	return (
		<div className="site-headline doctor-portal">
			<LoginForm visible={loginVisible} handleClose={() => setLoginVisible(false)} afterLogin={() => setLoginVisible(false)} />
			<div className="doctor-portal-container">
				<div className="px-50 mb20 doctor-portal-content ">
					<div className="max-content main-content">
						<div className="pull-right">
							<ShareLinks />
						</div>
						<BreadCrumbs
							links={[
								{
									title: 'Clinics',
									path: '/clinics',
								},
							]}
							title={name}
						/>
						<div className="mb20 px15 main-content-block clearfix">
							<div className="row">
								<div className="left-pull">
									<div className="avatar">
										<DoctorAvatar
											hideWhenLoading
											alt="Clinic avatar"
											withLink={false}
											src={getClinicAvatar(item, false)}
										/>
									</div>
									{item.socialMedias ? <SocialIcons
										className="doctor-portal__icons"
										socialMedias={item.socialMedias}
									/> : null}
								</div>
								<div className="text-start left-pull main-info">
									<div className="doctor-menu-content">
										<div>
											<h1 className="doctor-name">
												{name}
											</h1>
											<div>
												{item.specialties?.length > 0 && item.specialties.filter((item) => item)
													.map((item) => <SpecialtyTag
														key={item.id}
														tagClassName="title__second"
														specialty={item}
													/>)}
											</div>
											<div className="mt10">
												{item.procedures?.length > 0 && item.procedures?.filter((item) => item)
													.map((item) =>
														(<>
															{item.path
																? <NavLink to={`/procedures/${item.path}`}>
																	<Tag color="purple" key={item.id} className="title__second">
																		{item.name}
																	</Tag>
																</NavLink>
																: <Tag color="purple" key={item.id} className="title__second">
																	{item.name}
																</Tag>
															}
														</>))}
											</div>
										</div>
										<nav className="doctor-portal__nav">
											<ul>
												{navMenu.filter((value) => value.check(item)).map((item) =>
													<li key={item.id} className={item.className}>
														<a href={`${location?.pathname}#${item.id}`}>{item.title}</a>
													</li>)
												}
											</ul>
										</nav>
									</div>
								</div>
							</div>
						</div>
						<div className="doctor-other-content">
							<Reviews
								reviews={item?.topReviews}
								user={user}
								appointment={undefined}
								doctor={item as any}
								hideButton
								locationId={item.id}
							/>
							<div className="clinic-card__body">
								<div className="doctor-portal__about scroll-top" id="about">
									<h3>
										About
										{' '}
										{getLocationName(item, false, true)}
									</h3>

									{item.portalDescription
										? <div className="inner-page__headline column-md-4 mb10">
											<div className="column-md-4">
												<div dangerouslySetInnerHTML={{ __html: `${item.portalDescription}` }} />
											</div>
										</div> : <div className="mb10">-</div>
									}
								</div>
								<h4>Additional Information</h4>
								<Locations hideTitle hideLink locations={[item]} />
							</div>
							<div className="show-touch show-touch__block">
								<h3 id="bookOnline" className="scroll-top">Book Online</h3>
								<div className="schedule-appointment__form">
									<Tabs
										activeKey={tab}
										onChange={setTab}
										items={getItems(true)}
									/>
								</div>
							</div>
							{item.doctors.length > 0 && <div>
								<br />
								<h2 className="scroll-top" id="doctors">Providers</h2>
								<div className="home__container doctor-card-list" style={{ paddingLeft: 0, paddingRight: 0 }}>
									<div className="mb10 doctors-list">
										{item.doctors.map((doctor) => <DoctorCard
											doctor={doctor.doctor}
											key={doctor.id}
										/>)}
									</div>
								</div>
							</div>}
							<ItemsProvider<Special>
								type="special"
								loadRequest="specialsList"
								items={item?.specials}
								filters={{
									locationId: item.id,
									column: [
										{ caption: 'name', direction: SortingDirection.Ascending },
										{ caption: 'price', direction: SortingDirection.Default }],
								}}
								skipInitLoad={!!item?.specials}
							>
								{({ state: { items } }) => <>
									{items.length > 0 ? <div className="is-relative">
										<div className="scroll-top" id="specials" />
										<NavLink
											className="btn btn-primary btn-more pull-right"
											to={{ pathname: '/specials', search: `locationIds=[${item.id}]` }}
										>
											View All
										</NavLink>
										<SpecialsCarousel specials={items} location={item} count={3} titleAsH2 />
									</div> : null}
								</>}
							</ItemsProvider>
							<div className="scroll-top" id="reviews" />
							{item.totalReviewCount ? <AllReviews reviews={item?.reviews} locationId={item.id} doctor={item as any} /> : null}
						</div>
					</div>
					<div className="schedule-appointment__wrapper mobile-hide">
						<div className={`schedule-appointment__form ${
							isOpen ? 'schedule-appointment__open' : 'schedule-appointment__close'} ${
							tab === 'inquiry' ? 'schedule-appointment__inquiry-tab' : ''}`}
						>
							<button
								type="button"
								className="btn btn-sm btn-default schedule-appointment__button pull-right"
								onClick={toggle}
							>
								<span />
							</button>
							<Tabs
								activeKey={tab}
								onChange={setTab}
								items={getItems(false)}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

const ClinicPortalComponent: React.FC = () => {
	const { path } = useParams<{ path }>();

	return (
		<ServerPageProvider
			path="clinics"
			inner={(serverPage) =>
				<ItemProvider
					id={serverPage?.item?.id || 0}
					type="location"
					readonly
					additionalParams={{ path }}
					loadRequest="getClinic"
					item={serverPage?.item}
					skipInitLoad={!!serverPage?.item}
				>
					<ClinicInner />
				</ItemProvider>
			}
		/>
	);
};

export default ClinicPortalComponent;
